import type {
  StorefrontCart,
  StorefrontLineItem,
  StorefrontProduct,
} from '@/types/chord'
import type {
  CartFormatter,
  CheckoutFormatter,
  LineItemFormatter,
  ProductFormatter,
} from '@chordcommerce/analytics'

export const getProductUrl = (handle: string): string => {
  const base = typeof window !== 'undefined' ? window.location.origin : ''
  return `${base}/products/${handle}`
}

export const lineItemFormatter: LineItemFormatter<StorefrontLineItem> = (
  props,
) => {
  if (!props) throw new Error('lineItemFormatter is missing props')
  const { lineItem } = props
  const { cost, merchandise, quantity } = lineItem || {}
  const { product } = merchandise || {}
  const productUrl = product?.handle && getProductUrl(product.handle)

  return {
    brand: product?.vendor,
    image_url: product?.featuredImage?.url,
    name: product?.title,
    option_values: merchandise?.selectedOptions?.map((o) => o.value),
    price: Number(cost?.totalAmount?.amount) || 0,
    product_id: product?.id?.replace('gid://shopify/Product/', ''),
    quantity,
    sku: merchandise?.sku,
    slug: product?.handle,
    url: productUrl,
    variant: merchandise?.title?.toString(),
    variant_id: merchandise?.id?.replace('gid://shopify/ProductVariant/', ''),
  }
}

export const checkoutFormatter: CheckoutFormatter<StorefrontCart> = (props) => {
  if (!props) throw new Error('checkoutFormatter is missing props')

  const { checkout: cart } = props

  const coupon = cart?.discountCodes?.[0]
  const products = cart?.lines?.map((lineItem: any, i: number) => {
    return {
      ...lineItemFormatter({ lineItem }),
      position: i + 1,
    }
  })

  return {
    checkout_type: 'Shopify',
    coupon: coupon?.code,
    currency: 'USD',
    products,
    revenue: Number(cart?.cost?.subtotalAmount?.amount) || 0,
    tax: Number(cart?.cost?.totalTaxAmount?.amount) || 0,
    value: Number(cart?.cost?.totalAmount?.amount) || 0,
  }
}

export const cartFormatter: CartFormatter<StorefrontCart> = (props) => {
  if (!props) throw new Error('cartFormatter is missing props')

  const { cart } = props

  const products = cart?.lines?.map((lineItem: any, i: number) => {
    return {
      ...lineItemFormatter({ lineItem }),
      position: i + 1,
    }
  })

  return {
    cart_id: cart?.id?.replace('gid://shopify/Cart/', ''),
    currency: cart?.cost?.subtotalAmount?.currencyCode,
    products,
    value: Number(cart?.cost?.totalAmount?.amount) || 0,
  }
}

// @ts-ignore: There is an inconsistency for this formatter typing
export const productFormatter: ProductFormatter<StorefrontProduct> = ({
  product,
}) => {
  if (!product) throw new Error('productFormatter is missing product')
  const { variants = [], details, addons, meals } = product

  if (variants.length === 0) {
    throw new Error(`productFormatter cannot find a variant`)
  }

  // Sort variants by dynamically added selected attribute
  const variant = variants.sort((a: any, b: any) =>
    a === b ? 0 : a?.selected ? -1 : 1,
  )[0]

  return {
    brand: details?.vendor,
    image_url: details?.shopifyImageUrl,
    name: details?.title,
    position: 1,
    price: Number(variant?.price) || 0,
    product_id: details?.id?.toString(),
    quantity: 1,
    sku: variant?.sku,
    slug: product?.slug,
    url: product?.gid,
    variant: variant?.title,
    variant_id: variant?.id?.toString(),
    properties: {
      addons: addons,
      meals: meals,
    },
  }
}
